import React from "react";
import { EnvelopeIcon } from "@heroicons/react/24/solid";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";

import { dateFormatter } from "../../../../utils/dateFormatter";

import { FormInput } from "../../../shared/formElements/formInput/FormInput";
import { FormCheckboxChips } from "../../../shared/formElements/formCheckBoxChip/FormCheckBoxChip";

import { FormDynamicKeyValueList } from "../../../shared/formElements/formDynamicKeyValueList/FormDynamicKeyValueList";

import classes from "./bulkSpareLotItemDetail.module.css";
import { ChatPanel } from "../../../chatPanel/ChatPanel";
import { useNavigate } from "react-router-dom";
import { FormDropdown } from "../../../shared/formElements/formDropdown/FormDropdown";
import { FormTextarea } from "../../../shared/formElements/formTextarea/FormTextarea";

export const StatusBadge = ({ status }) => {
  const statusConfig = {
    approved: {
      label: "Approved",
      bg: "#E6F7EF",
      color: "#00A167",
      Icon: CheckCircleIcon,
    },
    pending: {
      label: "Pending",
      bg: "#FFF4E5",
      color: "#FF8C00",
      Icon: ClockIcon,
    },
    rejected: {
      label: "Rejected",
      bg: "#FDECEC",
      color: "#E53935",
      Icon: XCircleIcon,
    },
  };

  const config = statusConfig?.[status?.toLowerCase()];
  if (!config) return null;

  const { label, bg, color, Icon } = config;

  return (
    <div className={classes.status}>
      <h4 className={classes.status__title}>Status:</h4>
      <div
        className={classes.status__badge}
        style={{
          backgroundColor: bg,
          color,
        }}
      >
        <Icon style={{ width: 14, height: 14 }} />
        {label}
      </div>
    </div>
  );
};

export const BulkSpareLotItemDetailForm = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
}) => {
  /* =========================
     FORM SETUP
  ========================= */

  const {
    register,
    handleSubmit,

    formState: { isValid, isDirty, errors, dirtyFields },
  } = useForm({
    defaultValues: {
      ...initialValues,
      features:
        initialValues?.features?.length > 0
          ? initialValues.features
          : [{ name: "", value: "" }],
    },
    mode: "onChange",
  });

  /* =========================
     SUBMIT HANDLER
  ========================= */
  const hasChanges = Object.keys(dirtyFields).length > 0;

  const submitHandler = (data) => {
    if (!onSubmit) return;
    onSubmit(data);
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div className={classes.box}>
      {/* =========================
         INTRO SECTION
      ========================= */}

      <div className={`${classes.box__section} ${classes.box__intro}`}>
        <div
          className={`${classes.box__section__head} ${classes.box__intro__head}`}
        >
          <h1 className={classes.box__section__head__title}>
            Item Id: {initialValues?.id}
          </h1>

          {/* <h2 className={classes.box__section__head__subtitle}>
            Uploaded Via CSV on {dateFormatter(initialValues.uploaded_on)}
          </h2> */}

          <h1 className={classes.box__section__head__title}>
            Lot Id: {initialValues?.request_id}
          </h1>
        </div>
      </div>

      <div className={classes.box__lot}>
        <form
          className={classes.box__form}
          onSubmit={handleSubmit(submitHandler)}
        >
          <div className={classes.box__form__grid}>
            {" "}
            <div
              className={`${classes.box__section} ${classes.box__lot__info}`}
            >
              <div className={classes.box__section__head}>
                <h3 className={classes.box__section__head__title}>
                  Item Information
                </h3>
              </div>

              <div className={classes.box__grid}>
                <FormInput
                  id="request_id"
                  label="Lot Request Id"
                  register={register}
                  // validation={{
                  //   required: "Request Id is required",
                  // }}
                  // error={errors.request_id?.message}
                  // required
                  disabled
                />
                <FormInput
                  id="part"
                  label="Part"
                  register={register}
                  // validation={{
                  //   required: "part is required",
                  // }}
                  // error={errors.part?.message}
                  // required
                  disabled
                />
                <FormInput
                  id="brand"
                  label="Brand"
                  register={register}
                  // validation={{
                  //   required: "Brand is required",
                  // }}
                  // error={errors.brand?.message}
                  // required
                  disabled
                />
                <FormInput
                  id="model"
                  label="Model"
                  register={register}
                  // validation={{
                  //   required: "Model is required",
                  // }}
                  // error={errors.model?.message}
                  // required
                  disabled
                />
                <FormInput
                  id="color"
                  label="Color"
                  register={register}
                  // validation={{
                  //   required: "Color is required",
                  // }}
                  // error={errors.color?.message}
                  // required
                  disabled
                />
                <FormInput
                  id="condition"
                  label="Condition"
                  register={register}
                  validation={{
                    required: "Condition is required",
                  }}
                  error={errors.condition?.message}
                  required
                />
                <FormInput
                  id="price"
                  label="Price"
                  type="number"
                  register={register}
                  validation={{
                    required: "Price is required",
                  }}
                  error={errors.price?.message}
                  required
                />
                <FormInput
                  id="quantity"
                  label="Quantity"
                  type="number"
                  register={register}
                  validation={{
                    required: "Request Id is required",
                  }}
                  error={errors.quantity?.message}
                  required
                />
                <FormInput
                  id="retail_price_per_piece"
                  label="Price Per Piece"
                  type="number"
                  register={register}
                  validation={{
                    required: "Request Id is required",
                  }}
                  error={errors.retail_price_per_piece?.message}
                  required
                />
              </div>
            </div>
            <div className={`${classes.box__section} ${classes.box__feature}`}>
              <h3 className={classes.box__section__head__title}>Description</h3>
              <FormTextarea
                id="description"
                rows={8}
                label="Description"
                register={register}
                validation={{
                  required: "Description is required",
                  minLength: {
                    value: 10,
                    message: "Minimum 10 characters required",
                  },
                }}
                required
              />
            </div>
          </div>
          <button
            type="submit"
            className={classes.box__form__submitBtn}
            disabled={!isValid || !hasChanges || isSubmitting}
          >
            {isSubmitting ? "Updating Item..." : "Update Item"}
          </button>
        </form>
      </div>
    </div>
  );
};
